import React, { useState } from 'react';
import Stack from '../../spa/core-components/Stack';
import { AccordionItem, ButtonAccordionItem } from './AccordionItem';
import { Space } from '../../settings/spaces';
import { AccordionItemValue } from './AccordionItem/BaseAccordionItem';

export interface AccordionProps {
    children: React.ReactElement[];
    singleOpen?: boolean;
    openFirstByDefault?: boolean;
    spaceBetween?: Space;
}

export const Accordion = ({
    children,
    singleOpen = false,
    openFirstByDefault = false,
    spaceBetween = 'none',
}: AccordionProps) => {
    const [itemsOpenStatus, setItemsOpenStatus] = useState<{ [key: number]: boolean }>(
        openFirstByDefault ? { 0: true } : {}
    );

    const toggleItem = (index: number) => {
        setItemsOpenStatus((prevState: { [key: number]: boolean }) => ({
            ...(singleOpen ? {} : prevState),
            [index]: !prevState[index],
        }));
    };

    return (
        <div data-testid="accordion">
            <Stack space={spaceBetween}>
                {children.map(({ type, props }, index) => {
                    return React.createElement(type, {
                        ...props,
                        key: `item-${index}`,
                        isFirst: index === 0,
                        isLast: index === children.length - 1,
                        isOpen: itemsOpenStatus[index],
                        onClick: () => toggleItem(index),
                    });
                })}
            </Stack>
        </div>
    );
};

interface AccordionItemData {
    id: string;
    title: string;
    child: React.ReactNode;
}

export interface FAQAccordionProps {
    data: AccordionItemData[];
    onItemOpen?: (accordionItemValue: AccordionItemValue) => void;
}

export const FAQAccordion = ({ data, onItemOpen }: FAQAccordionProps) => (
    <Accordion>
        {data.map((item, index) => (
            <AccordionItem
                key={`item-${index}`}
                id={item.id}
                title={item.title}
                onOpen={onItemOpen}
            >
                {item.child}
            </AccordionItem>
        ))}
    </Accordion>
);

export interface FeatureAccordionProps {
    data: AccordionItemData[];
    featureColor?: string;
    onItemOpen?: (accordionItemValue: AccordionItemValue) => void;
}

export const FeatureAccordion = ({ data, featureColor, onItemOpen }: FeatureAccordionProps) => (
    <Accordion singleOpen={true} openFirstByDefault={true} spaceBetween="space7">
        {data.map((item, index) => (
            <ButtonAccordionItem
                key={`item-${index}`}
                id={item.id}
                title={item.title}
                featureColor={featureColor}
                onOpen={onItemOpen}
            >
                {item.child}
            </ButtonAccordionItem>
        ))}
    </Accordion>
);

export default Accordion;
